import Tag from '../@types/tag';
import {FaultTree} from '../@types/faultTree';
import {FaultTreeNode} from '../@types/faultTreeNode';

export interface MinimalCutSet {
    baseEventIds: Set<number>;
    order: number;
    probability: number;
}

export interface MinimalCutSetsResult {
    cutSets: MinimalCutSet[];
    truncated: boolean;
    rootNodeId: number;
    rootNodeName: string;
    totalBaseEvents: number;
    computationTimeMs: number;
    warnings: string[];
}

export interface MCSOptions {
    maxCutSets: number;
    maxOrder: number;
    maxDepth: number;
}

const DEFAULT_OPTIONS: MCSOptions = {
    maxCutSets: 3000,
    maxOrder: 10,
    maxDepth: 100,
};

// ---------------------------------------------------------------------------
// Common Cause Failure (CCF) Analysis types
// ---------------------------------------------------------------------------

export type CCFModelType = 'none' | 'beta-factor' | 'alpha-factor' | 'mgl';

export interface CCFGroupDetected {
    tagId: number;
    tagName: string;
    tagColor: string | undefined;
    memberNodeIds: Set<number>;
    memberCount: number;
}

export interface BetaFactorParams { model: 'beta-factor'; beta: number; }
export interface AlphaFactorParams { model: 'alpha-factor'; alphas: number[]; }
export interface MGLParams { model: 'mgl'; beta: number; gamma: number; delta: number; }
export type CCFGroupParams = BetaFactorParams | AlphaFactorParams | MGLParams;

export interface CCFGroup {
    detected: CCFGroupDetected;
    params: CCFGroupParams;
}

export interface AdjustedCutSet {
    originalIndex: number;
    baseEventIds: Set<number>;
    order: number;
    originalProbability: number;
    adjustedProbability: number;
    isImplicitCCF: boolean;
    ccfSourceTagName: string | null;
}

export interface CCFAnalysisResult {
    adjustedCutSets: AdjustedCutSet[];
    originalTopEventProbability: number;
    adjustedTopEventProbability: number;
    implicitCutSetCount: number;
    groupsApplied: number;
}

/**
 * Compute Minimal Cut Sets for a fault tree starting from a given node.
 * A minimal cut set is the smallest combination of base event failures
 * that causes the top event to fail.
 */
export function computeMinimalCutSets(
    faultTree: FaultTree,
    startNodeId: number,
    options?: Partial<MCSOptions>
): MinimalCutSetsResult {
    const startTime = performance.now();
    const opts: MCSOptions = { ...DEFAULT_OPTIONS, ...options };
    const warnings: string[] = [];

    // Build node lookup map
    const nodeMap = new Map<number, FaultTreeNode>();
    for (const node of faultTree.faultTreeNodes) {
        nodeMap.set(node.id, node);
    }

    const startNode = nodeMap.get(startNodeId);
    if (!startNode) {
        return {
            cutSets: [],
            truncated: false,
            rootNodeId: startNodeId,
            rootNodeName: 'Unknown',
            totalBaseEvents: 0,
            computationTimeMs: performance.now() - startTime,
            warnings: ['Start node not found in fault tree'],
        };
    }

    // Compute raw cut sets
    const rawCutSets = mcsForNode(startNodeId, nodeMap, opts, 0, warnings);

    // Prune non-minimal
    const minimal = pruneNonMinimal(rawCutSets);

    // Sort by probability descending
    const withProbabilities = minimal.map((cs): MinimalCutSet => ({
        baseEventIds: cs,
        order: cs.size,
        probability: computeCutSetProbability(cs, nodeMap),
    }));
    withProbabilities.sort((a, b) => b.probability - a.probability);

    // Truncate if needed
    const truncated = withProbabilities.length > opts.maxCutSets;
    const result = truncated ? withProbabilities.slice(0, opts.maxCutSets) : withProbabilities;

    // Count unique base events across all cut sets
    const allBaseEvents = new Set<number>();
    for (const cs of result) {
        for (const id of cs.baseEventIds) {
            allBaseEvents.add(id);
        }
    }

    return {
        cutSets: result,
        truncated,
        rootNodeId: startNodeId,
        rootNodeName: startNode.uniqueId || startNode.name,
        totalBaseEvents: allBaseEvents.size,
        computationTimeMs: performance.now() - startTime,
        warnings,
    };
}

/**
 * Recursively compute cut sets for a given node.
 * - BASE_EVENT or leaf (no children): returns [{nodeId}]
 * - OR / XOR gate: union (concat) of children's cut sets, then prune
 * - AND gate: cross-product of children's cut sets, then prune
 */
function mcsForNode(
    nodeId: number,
    nodeMap: Map<number, FaultTreeNode>,
    options: MCSOptions,
    depth: number,
    warnings: string[]
): Set<number>[] {
    if (depth > options.maxDepth) {
        return [];
    }

    const node = nodeMap.get(nodeId);
    if (!node) {
        // Node not in map (possibly not loaded) - treat as base event conservatively
        warnings.push(`Node ID ${nodeId} not found in loaded nodes; treated as base event`);
        return [new Set([nodeId])];
    }

    const type = node.type?.toUpperCase() || '';

    // Base event or leaf node with no children
    if (type === 'BASE_EVENT' || !node.childIds || node.childIds.length === 0) {
        return [new Set([node.id])];
    }

    // Recurse into children
    const childCutSets: Set<number>[][] = [];
    for (const childId of node.childIds) {
        const childResult = mcsForNode(childId, nodeMap, options, depth + 1, warnings);
        if (childResult.length > 0) {
            childCutSets.push(childResult);
        }
    }

    if (childCutSets.length === 0) {
        return [new Set([node.id])];
    }

    let result: Set<number>[];

    if (type === 'OR' || type === 'XOR') {
        // OR gate: union (concatenation) of all children's cut sets.
        // XOR is approximated as OR (conservative) — the "both fail" case is
        // included, which overapproximates risk but is standard for coherent analysis.
        result = [];
        for (const childSets of childCutSets) {
            for (const cs of childSets) {
                result.push(cs);
            }
            // Prune during OR accumulation to prevent blowout
            if (result.length > options.maxCutSets) {
                result = pruneNonMinimal(result);
            }
        }
    } else if (type === 'AND') {
        // AND gate: cross-product of children's cut sets.
        // Sort children by cut set count ascending so the smallest inputs are
        // folded first, minimizing intermediate result size.
        childCutSets.sort((a, b) => a.length - b.length);
        result = childCutSets[0];
        for (let i = 1; i < childCutSets.length; i++) {
            result = crossProduct(result, childCutSets[i], options);
        }
    } else {
        // Unknown gate type - treat as OR (conservative)
        warnings.push(`Unknown gate type "${type}" for node ${node.uniqueId}; treated as OR`);
        result = [];
        for (const childSets of childCutSets) {
            for (const cs of childSets) {
                result.push(cs);
            }
            // Prune during accumulation to prevent blowout
            if (result.length > options.maxCutSets) {
                result = pruneNonMinimal(result);
            }
        }
    }

    // Intermediate pruning to prevent blowout
    if (result.length > options.maxCutSets) {
        result = pruneNonMinimal(result);
        if (result.length > options.maxCutSets) {
            result = result.slice(0, options.maxCutSets);
        }
    }

    return result;
}

/**
 * Cross-product of two collections of cut sets.
 * Each result is the union of one set from a and one set from b.
 */
function crossProduct(
    a: Set<number>[],
    b: Set<number>[],
    options: MCSOptions
): Set<number>[] {
    const result: Set<number>[] = [];
    for (const setA of a) {
        for (const setB of b) {
            const merged = new Set([...setA, ...setB]);
            if (merged.size <= options.maxOrder) {
                result.push(merged);
                if (result.length > options.maxCutSets) {
                    return result;
                }
            }
        }
    }
    return result;
}

/**
 * Remove non-minimal cut sets.
 * A cut set is non-minimal if it is a proper superset of another cut set.
 */
function pruneNonMinimal(cutSets: Set<number>[]): Set<number>[] {
    // Sort by size ascending so we check smaller sets first
    cutSets.sort((a, b) => a.size - b.size);
    const accepted: Set<number>[] = [];

    for (const candidate of cutSets) {
        let isSuperset = false;
        for (const existing of accepted) {
            // accepted is built in ascending size order; once size >= candidate, no existing can be a proper subset
            if (existing.size >= candidate.size) break;
            if (isSubsetOf(existing, candidate)) {
                isSuperset = true;
                break;
            }
        }
        if (!isSuperset) {
            accepted.push(candidate);
        }
    }

    return accepted;
}

/**
 * Returns true if every element of `a` is in `b`.
 */
function isSubsetOf(a: Set<number>, b: Set<number>): boolean {
    if (a.size > b.size) return false;
    for (const item of a) {
        if (!b.has(item)) return false;
    }
    return true;
}

/**
 * Compute the probability of a cut set as the product of base event probabilities.
 */
function computeCutSetProbability(
    baseEventIds: Set<number>,
    nodeMap: Map<number, FaultTreeNode>
): number {
    let probability = 1;
    for (const id of baseEventIds) {
        const node = nodeMap.get(id);
        if (node) {
            probability *= node.probability ?? 0;
        } else {
            probability = 0;
        }
    }
    return probability;
}

// ---------------------------------------------------------------------------
// Common Cause Failure (CCF) Analysis functions
// ---------------------------------------------------------------------------

/**
 * Detect potential CCF groups by finding tags shared by 2+ base events in cut sets.
 */
export function detectCCFGroups(
    cutSets: MinimalCutSet[],
    tagLinksByFtNodeId: Map<number, number[]>,
    tagById: Map<number, Tag>,
): CCFGroupDetected[] {
    // 1. For each base event in order 2+ cut sets, look up its tags.
    //    Build tag → base event set mapping, but only for base events
    //    that co-occur with another same-tagged event in at least one cut set.
    //    Order-1 cut sets are skipped — CCF doesn't add risk to single failures.

    // First pass: build tag → all base event IDs across order 2+ cut sets
    const tagToAllNodes = new Map<number, Set<number>>();
    for (const cs of cutSets) {
        if (cs.order < 2) continue;
        for (const nodeId of cs.baseEventIds) {
            const tagIds = tagLinksByFtNodeId.get(nodeId);
            if (!tagIds) continue;
            for (const tagId of tagIds) {
                let nodeSet = tagToAllNodes.get(tagId);
                if (!nodeSet) {
                    nodeSet = new Set<number>();
                    tagToAllNodes.set(tagId, nodeSet);
                }
                nodeSet.add(nodeId);
            }
        }
    }

    // Second pass: for each tag with 2+ members, build a co-occurrence graph
    // and split into connected components so that independent clusters of
    // co-occurring members form separate CCF groups.
    const groups: CCFGroupDetected[] = [];
    for (const [tagId, allNodeIds] of tagToAllNodes) {
        if (allNodeIds.size < 2) continue;

        // Build adjacency list from cut-set co-occurrence
        const adj = new Map<number, Set<number>>();
        for (const cs of cutSets) {
            if (cs.order < 2) continue;
            const membersInCutSet: number[] = [];
            for (const nodeId of cs.baseEventIds) {
                if (allNodeIds.has(nodeId)) {
                    membersInCutSet.push(nodeId);
                }
            }
            if (membersInCutSet.length >= 2) {
                for (let i = 0; i < membersInCutSet.length; i++) {
                    for (let j = i + 1; j < membersInCutSet.length; j++) {
                        const a = membersInCutSet[i];
                        const b = membersInCutSet[j];
                        if (!adj.has(a)) adj.set(a, new Set<number>());
                        if (!adj.has(b)) adj.set(b, new Set<number>());
                        adj.get(a)!.add(b);
                        adj.get(b)!.add(a);
                    }
                }
            }
        }

        if (adj.size < 2) continue;

        // BFS to find connected components
        const visited = new Set<number>();
        const tag = tagById.get(tagId);
        if (!tag) continue;
        for (const startNode of adj.keys()) {
            if (visited.has(startNode)) continue;
            const component = new Set<number>();
            const queue = [startNode];
            while (queue.length > 0) {
                const node = queue.pop()!;
                if (visited.has(node)) continue;
                visited.add(node);
                component.add(node);
                const neighbors = adj.get(node);
                if (neighbors) {
                    for (const n of neighbors) {
                        if (!visited.has(n)) queue.push(n);
                    }
                }
            }
            if (component.size >= 2) {
                groups.push({
                    tagId,
                    tagName: tag.name,
                    tagColor: tag.presentation.color,
                    memberNodeIds: component,
                    memberCount: component.size,
                });
            }
        }
    }

    // Sort by member count desc, then tag name asc
    groups.sort((a, b) => {
        if (b.memberCount !== a.memberCount) return b.memberCount - a.memberCount;
        return a.tagName.localeCompare(b.tagName);
    });

    return groups;
}

/**
 * Adaptive representative probability for a CCF group.
 * Uses geometric mean when member probabilities are similar (within ~2 orders
 * of magnitude), otherwise uses the maximum member probability for a more
 * conservative estimate.
 */
function computeRepresentativeProbability(
    memberNodeIds: Set<number>,
    nodeMap: Map<number, FaultTreeNode>,
): number {
    const probs: number[] = [];
    for (const id of memberNodeIds) {
        const node = nodeMap.get(id);
        const p = node?.probability ?? 0;
        if (p > 0) probs.push(p);
    }
    if (probs.length === 0) return 0;

    let maxP = probs[0];
    let minP = probs[0];
    for (let i = 1; i < probs.length; i++) {
        if (probs[i] > maxP) maxP = probs[i];
        if (probs[i] < minP) minP = probs[i];
    }

    // When probabilities span more than ~2 orders of magnitude,
    // use the max for a conservative estimate
    if (maxP / minP > 100) return maxP;

    // Otherwise geometric mean represents the group well
    const logSum = probs.reduce((sum, p) => sum + Math.log(p), 0);
    return Math.exp(logSum / probs.length);
}

/**
 * Count how many members of a CCF group appear in a cut set.
 */
function countGroupMembers(baseEventIds: Set<number>, memberNodeIds: Set<number>): number {
    let count = 0;
    for (const id of baseEventIds) {
        if (memberNodeIds.has(id)) count++;
    }
    return count;
}

/**
 * Apply Beta-Factor model adjustments to cut sets and generate implicit CCF cut sets.
 */
function applyBetaFactor(
    adjustedCutSets: AdjustedCutSet[],
    group: CCFGroup,
    nodeMap: Map<number, FaultTreeNode>,
): AdjustedCutSet[] {
    const beta = Math.max(0, Math.min(1, (group.params as BetaFactorParams).beta));
    const memberIds = group.detected.memberNodeIds;
    const q = computeRepresentativeProbability(memberIds, nodeMap);

    // Scale existing cut sets with 2+ group members
    for (const acs of adjustedCutSets) {
        if (acs.isImplicitCCF) continue;
        const k = countGroupMembers(acs.baseEventIds, memberIds);
        if (k >= 2) {
            acs.adjustedProbability *= Math.pow(1 - beta, k);
        }
    }

    // Generate implicit CCF cut set (all members fail together)
    if (q > 0) {
        const implicitP = beta * q;
        const implicitCutSet: AdjustedCutSet = {
            originalIndex: -1,
            baseEventIds: new Set(memberIds),
            order: memberIds.size,
            originalProbability: 0,
            adjustedProbability: implicitP,
            isImplicitCCF: true,
            ccfSourceTagName: group.detected.tagName,
        };
        adjustedCutSets.push(implicitCutSet);
    }

    return adjustedCutSets;
}

/**
 * Apply Alpha-Factor model adjustments to cut sets and generate implicit CCF cut sets.
 */
function applyAlphaFactor(
    adjustedCutSets: AdjustedCutSet[],
    group: CCFGroup,
    nodeMap: Map<number, FaultTreeNode>,
): AdjustedCutSet[] {
    const params = group.params as AlphaFactorParams;
    const alphas = params.alphas.map((a) => Math.max(0, a));
    const m = group.detected.memberCount;
    const memberIds = group.detected.memberNodeIds;
    const q = computeRepresentativeProbability(memberIds, nodeMap);

    // αₜ = Σ(k × αₖ) for k=1..m
    let alphaT = 0;
    for (let k = 0; k < alphas.length && k < m; k++) {
        alphaT += (k + 1) * alphas[k];
    }
    if (alphaT === 0) return adjustedCutSets;

    // Q₁ = (m × α₁ / αₜ) × Qₜ — independent failure probability
    const ratio = q > 0 ? Math.min((m * alphas[0]) / alphaT, 1) : 0;

    // Scale existing cut sets
    for (const acs of adjustedCutSets) {
        if (acs.isImplicitCCF) continue;
        const j = countGroupMembers(acs.baseEventIds, memberIds);
        if (j >= 2 && ratio > 0) {
            acs.adjustedProbability *= Math.pow(ratio, j);
        }
    }

    // Implicit cut set: P = αₘ/αₜ × Qₜ
    const alphaM = m <= alphas.length ? alphas[m - 1] : 0;
    if (q > 0 && alphaM > 0) {
        const implicitP = (alphaM / alphaT) * q;
        adjustedCutSets.push({
            originalIndex: -1,
            baseEventIds: new Set(memberIds),
            order: memberIds.size,
            originalProbability: 0,
            adjustedProbability: implicitP,
            isImplicitCCF: true,
            ccfSourceTagName: group.detected.tagName,
        });
    }

    return adjustedCutSets;
}

/**
 * Apply MGL (Multiple Greek Letter) model adjustments to cut sets and generate implicit CCF cut sets.
 */
function applyMGL(
    adjustedCutSets: AdjustedCutSet[],
    group: CCFGroup,
    nodeMap: Map<number, FaultTreeNode>,
): AdjustedCutSet[] {
    const params = group.params as MGLParams;
    const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
    const beta = clamp01(params.beta);
    const gamma = clamp01(params.gamma);
    const delta = clamp01(params.delta);
    const memberIds = group.detected.memberNodeIds;
    const m = group.detected.memberCount;
    const q = computeRepresentativeProbability(memberIds, nodeMap);

    // Independent portion: same as Beta-Factor
    for (const acs of adjustedCutSets) {
        if (acs.isImplicitCCF) continue;
        const k = countGroupMembers(acs.baseEventIds, memberIds);
        if (k >= 2) {
            acs.adjustedProbability *= Math.pow(1 - beta, k);
        }
    }

    // Implicit all-fail cut set: m=2→βq, m=3→βγq, m=4→βγδq
    if (q > 0) {
        let implicitP = beta * q;
        if (m >= 3) implicitP *= gamma;
        if (m >= 4) implicitP *= delta;

        adjustedCutSets.push({
            originalIndex: -1,
            baseEventIds: new Set(memberIds),
            order: memberIds.size,
            originalProbability: 0,
            adjustedProbability: implicitP,
            isImplicitCCF: true,
            ccfSourceTagName: group.detected.tagName,
        });
    }

    return adjustedCutSets;
}

/**
 * Apply CCF analysis to computed cut sets and return adjusted results.
 */
export function applyCCFAnalysis(
    cutSets: MinimalCutSet[],
    ccfGroups: CCFGroup[],
    nodeMap: Map<number, FaultTreeNode>,
): CCFAnalysisResult {
    // 1. Clone each original cut set into an AdjustedCutSet
    let adjustedCutSets: AdjustedCutSet[] = cutSets.map((cs, i) => ({
        originalIndex: i + 1,
        baseEventIds: new Set(cs.baseEventIds),
        order: cs.order,
        originalProbability: cs.probability,
        adjustedProbability: cs.probability,
        isImplicitCCF: false,
        ccfSourceTagName: null,
    }));

    // 2. Apply each CCF group's model (skip duplicate member sets)
    let groupsApplied = 0;
    const processedMemberSets = new Set<string>();
    for (const group of ccfGroups) {
        const memberKey = Array.from(group.detected.memberNodeIds).sort((a, b) => a - b).join(',');
        if (processedMemberSets.has(memberKey)) continue;
        processedMemberSets.add(memberKey);
        switch (group.params.model) {
            case 'beta-factor':
                adjustedCutSets = applyBetaFactor(adjustedCutSets, group, nodeMap);
                groupsApplied++;
                break;
            case 'alpha-factor':
                adjustedCutSets = applyAlphaFactor(adjustedCutSets, group, nodeMap);
                groupsApplied++;
                break;
            case 'mgl':
                adjustedCutSets = applyMGL(adjustedCutSets, group, nodeMap);
                groupsApplied++;
                break;
        }
    }

    // Sort: implicit CCF first (by probability desc), then originals (by probability desc)
    adjustedCutSets.sort((a, b) => {
        if (a.isImplicitCCF !== b.isImplicitCCF) return a.isImplicitCCF ? -1 : 1;
        return b.adjustedProbability - a.adjustedProbability;
    });

    // 3. Sum probabilities for top event approximation (rare-event)
    const originalTopEventProbability = cutSets.reduce((sum, cs) => sum + cs.probability, 0);
    const adjustedTopEventProbability = adjustedCutSets.reduce((sum, acs) => sum + acs.adjustedProbability, 0);
    const implicitCutSetCount = adjustedCutSets.filter((acs) => acs.isImplicitCCF).length;

    return {
        adjustedCutSets,
        originalTopEventProbability,
        adjustedTopEventProbability,
        implicitCutSetCount,
        groupsApplied,
    };
}
